import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, ChefHat, Search, Filter, BookOpen, ArrowRight, Fish } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './RecipesPage.css';

const DIFFICULTY_COLOR = { EASY: '#10B981', MEDIUM: '#F59E0B', HARD: '#EF4444' };

const RecipesPage = () => {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('ALL');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recipesRes, catsRes] = await Promise.all([
        axiosInstance.get('/recipes'),
        axiosInstance.get('/recipes/categories'),
      ]);
      if (recipesRes.data.success) setRecipes(recipesRes.data.data);
      if (catsRes.data.success) setCategories(catsRes.data.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const filtered = recipes.filter(r => {
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.description?.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'ALL' || r.category === activeCategory;
    return matchSearch && matchCat;
  });

  return (
    <>
      <Navbar />
      <div className="recipes-page">
        {/* Hero */}
        <section className="recipes-hero">
          <div className="container">
            <div className="recipes-hero-badge"><ChefHat size={14} /> Aurora Recipes</div>
            <h1 className="recipes-hero-title">Cook Like a <span>Seafood Chef</span></h1>
            <p className="recipes-hero-sub">Discover delicious recipes using Aurora's fresh seafood. From quick weeknight dinners to impressive weekend feasts.</p>
            <div className="recipes-search">
              <Search size={18} className="recipes-search-icon" />
              <input type="text" placeholder="Search recipes..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="recipes-cats-section">
          <div className="container">
            <div className="recipes-cats">
              <button className={`recipes-cat-btn ${activeCategory === 'ALL' ? 'active' : ''}`} onClick={() => setActiveCategory('ALL')}>All Recipes</button>
              {categories.map(cat => (
                <button key={cat} className={`recipes-cat-btn ${activeCategory === cat ? 'active' : ''}`} onClick={() => setActiveCategory(cat)}>{cat}</button>
              ))}
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="recipes-grid-section">
          <div className="container">
            {loading ? (
              <div className="recipes-loading">
                <div className="spinner" />
                <p>Loading recipes...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="recipes-empty">
                <Fish size={48} color="#29ABE2" strokeWidth={1} />
                <h3>No recipes found</h3>
                <p>Try a different search or category.</p>
              </div>
            ) : (
              <div className="recipes-grid">
                {filtered.map(recipe => (
                  <Link to={`/recipes/${recipe.id}`} key={recipe.id} className="recipe-card">
                    <div className="recipe-card-image">
                      {recipe.imageUrl ? (
                        <img src={recipe.imageUrl} alt={recipe.title} />
                      ) : (
                        <div className="recipe-card-placeholder"><Fish size={40} color="#29ABE2" strokeWidth={1} /></div>
                      )}
                      {recipe.difficulty && (
                        <span className="recipe-difficulty-badge" style={{ background: DIFFICULTY_COLOR[recipe.difficulty] }}>
                          {recipe.difficulty}
                        </span>
                      )}
                    </div>
                    <div className="recipe-card-body">
                      {recipe.category && <p className="recipe-category">{recipe.category}</p>}
                      <h3 className="recipe-title">{recipe.title}</h3>
                      <p className="recipe-desc">{recipe.description?.slice(0, 90)}{recipe.description?.length > 90 ? '...' : ''}</p>
                      <div className="recipe-meta">
                        {recipe.prepTimeMinutes && <span><Clock size={13} /> {recipe.prepTimeMinutes + (recipe.cookTimeMinutes || 0)} min</span>}
                        {recipe.servings && <span><Users size={13} /> {recipe.servings} servings</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Request CTA */}
        <section className="recipes-request-cta">
          <div className="container">
            <div className="recipes-request-box">
              <BookOpen size={32} color="#29ABE2" strokeWidth={1.5} />
              <div>
                <h3>Can't Find What You're Looking For?</h3>
                <p>Request a specific recipe from our team. Login to your dashboard and submit a recipe request — our chefs will create it for you!</p>
              </div>
              <a href="https://app.auroragh.com/dashboard/recipes" className="hero-btn-primary">
                Request a Recipe <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default RecipesPage;